import { useState, FormEvent } from 'react';
import { X, Upload, Eye, EyeOff } from 'lucide-react';
import { uploadCertificado } from '../api/certificado';
import { apiError } from '../api/client';

interface Props {
  emitenteId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CertificadoUploadModal({ emitenteId, onClose, onSuccess }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Selecione o arquivo .pfx / .p12');
      return;
    }
    if (!senha) {
      setError('Informe a senha do certificado');
      return;
    }
    setLoading(true);
    try {
      await uploadCertificado(emitenteId, file, senha);
      onSuccess();
    } catch (err) {
      setError(apiError(err, 'Erro ao enviar certificado'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/45 p-4 backdrop-blur-sm dark:bg-black/70"
      role="dialog"
      aria-modal="true"
    >
      <div className="card-surface w-full max-w-md overflow-hidden shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div>
            <div className="eyebrow">Certificado A1</div>
            <h3 className="font-semibold text-slate-950 dark:text-slate-100">Enviar certificado digital</h3>
          </div>
          <button onClick={onClose} className="btn-ghost !px-2 !py-1 h-9 w-9" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="field-label">Arquivo .pfx / .p12</label>
            <input
              type="file"
              accept=".pfx,.p12,application/x-pkcs12"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700 hover:file:bg-slate-200 dark:text-slate-300 dark:file:bg-white/10 dark:file:text-white dark:hover:file:bg-white/20"
            />
          </div>
          <div>
            <label className="field-label">Senha</label>
            <div className="relative">
              <input
                type={mostrar ? 'text' : 'password'}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="field-input pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setMostrar((v) => !v)}
                className="absolute inset-y-0 right-0 px-3 text-neutral-500 dark:text-neutral-400"
                aria-label="Mostrar senha"
              >
                {mostrar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && (
            <div className="text-sm text-danger bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="-mx-4 -mb-4 flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/70">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              <Upload className="h-4 w-4" />
              {loading ? 'Enviando...' : 'Validar e salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
